import React, { useMemo, useState, useEffect } from 'react'

// @MUI
import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Modal from '@mui/material/Modal'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import {
  Alert,
  Autocomplete,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormHelperText,
  Snackbar,
  Stack,
  Switch,
  Divider,
} from '@mui/material'
import API from '../../../services/API'
import CustomDataGrid from '../../../components/DataGrid/CustomDataGrid'
import SingleBaseLocationSelect from '../../../components/SingleBaseLocationSelect'
import AddIcon from '@mui/icons-material/Add'
import BackpackIcon from '@mui/icons-material/Backpack'
import Inventory2Icon from '@mui/icons-material/Inventory2'
import WarehouseIcon from '@mui/icons-material/Warehouse'
import TuneIcon from '@mui/icons-material/Tune'
import { CheckCircle } from '@mui/icons-material'

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: { xs: 'calc(100vw - 2rem)', sm: 440 },
  bgcolor: 'background.paper',
  borderRadius: 2,
  boxShadow: 24,
  borderColor: 'none',
  maxHeight: '85vh',
  overflow: 'auto',
  padding: 2,
  outline: 'none',
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
}

const panelSx = {
  borderRadius: 2,
  border: '1px solid',
  borderColor: 'divider',
  boxShadow: '0 12px 28px rgba(17, 24, 39, 0.06)',
  overflow: 'hidden',
}

const MedicineStores = () => {
  const [rows, setRows] = useState([])
  const [filter, setFilter] = useState(null)
  const [fetchError, setFetchError] = useState()
  const [addMode, setAddMode] = useState(false)
  const [newLocation, setNewLocation] = useState('')
  const [newType, setNewType] = useState('Medicine Bag')
  const [newBagLevel, setNewBagLevel] = useState('Intermediate')
  const [newAllowExpired, setNewAllowExpired] = useState(false)
  // Mobile store taken to events - can be administered from directly
  const [newAllowAdministration, setNewAllowAdministration] = useState(false)
  // Every store and bag belongs to a base location
  const [newBaseLocation, setNewBaseLocation] = useState(null)
  const [submitError, setSubmitError] = useState(false)
  const [submitSuccess, setSubmitSuccess] = useState(false)
  const [selectedStore, setSelectedStore] = useState(false)
  const [bagExists, setBagExists] = useState(null)
  const [bagLevelOptions, setBagLevelOptions] = useState([
    'General',
    'Intermediate',
    'Advanced',
    'Specialist',
    'Other',
  ])

  useEffect(() => {
    API.get('/siteConfig/medicines/locations')
      .then((res) => setRows(res.data))
      .catch(() => setFetchError(true))
    API.get('/settings?select=medsmanagement.bagLevels').then((res) => {
      if (res.status === 200 && res?.data?.medsmanagement?.bagLevels?.length) {
        const options = res.data.medsmanagement.bagLevels
          .map((level) => {
            if (typeof level === 'string') return level
            return level?.name || ''
          })
          .filter(Boolean)

        setBagLevelOptions(options)
      }
    })
  }, [selectedStore])

  const handleSubmitNew = (e, { undoArchive } = {}) => {
    e.preventDefault()
    if (selectedStore) {
      API.put('/siteConfig/medicines/location', {
        ...selectedStore,
        name: newLocation,
        type: newType,
        level: newType === 'Medicine Bag' ? newBagLevel : '',
        baseLocation: newBaseLocation?._id,
        allowExpiredMedicines: newType === 'Medicine Store' && newAllowExpired,
        allowAdministration:
          newType === 'Medicine Store' && newAllowAdministration,
      })
        .then((res) => {
          if (res.status === 200) {
            setRows(res.data)
            setAddMode(false)
            setSelectedStore(false)
            setSubmitSuccess(true)
          } else {
            setSubmitError(true)
          }
        })
        .catch(() => setSubmitError(true))
    } else {
      API.post('/siteConfig/medicines/location', {
        undoArchive,
        name: newLocation,
        type: newType,
        level: newType === 'Medicine Bag' && newBagLevel,
        baseLocation: newBaseLocation?._id,
        allowExpiredMedicines: newType === 'Medicine Store' && newAllowExpired,
        allowAdministration:
          newType === 'Medicine Store' && newAllowAdministration,
      })
        .then((res) => {
          if (res.status === 200) {
            setRows(res.data)
            setAddMode(false)
            setSubmitSuccess(true)
            setBagExists(null)
            setNewLocation('')
          } else if (res.status === 400 && res.data?.existing) {
            setBagExists(res.data.existing)
          } else {
            setSubmitError(true)
          }
        })
        .catch(() => setSubmitError(true))
    }
  }

  const filteredRows = () => {
    switch (filter) {
      case 'bags':
        return rows.filter((row) => row.type === 'Medicine Bag')
      case 'stores':
        return rows.filter((row) => row.type === 'Medicine Store')
      case 'inUse':
        return rows.filter((row) => row.type === 'Medicine Bag' && row.level)
      default:
        return rows
    }
  }

  const sortedRows = [...filteredRows()].sort((a, b) =>
    (a.name ?? '').localeCompare(b.name ?? '', undefined, {
      numeric: true,
      sensitivity: 'base',
    }),
  )

  const locationStats = useMemo(() => {
    const bags = rows.filter((row) => row.type === 'Medicine Bag')
    const stores = rows.filter((row) => row.type === 'Medicine Store')
    const levels = new Set(bags.map((bag) => bag.level).filter(Boolean))

    return {
      total: rows.length,
      bags: bags.length,
      stores: stores.length,
      levels: levels.size,
    }
  }, [rows])

  const LocationStat = ({ icon, label, value, filterName }) => (
    <Stack
      direction="row"
      alignItems="center"
      gap={1}
      sx={{
        p: 1.25,
        borderRadius: 1,
        border: '1px solid',
        borderColor: 'divider',
        minWidth: { xs: '100%', sm: 160 },
        bgcolor: filter === filterName ? 'contrast.main' : 'background.paper',
        cursor: 'pointer',
      }}
      onClick={() => filterName !== undefined && setFilter(filterName)}
    >
      <Box
        sx={{
          width: 34,
          height: 34,
          borderRadius: 1,
          display: 'grid',
          placeItems: 'center',
          bgcolor: filter === filterName ? 'divider' : '#E8F3F7',
          color: filter === filterName ? 'contrast.main' : 'anBlue.main',
        }}
      >
        {icon}
      </Box>
      <Stack
        width="100%"
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <Typography
          variant="caption"
          color={filter === filterName ? 'white' : 'text.secondary'}
        >
          {label}
        </Typography>
        <Typography
          variant="title3"
          lineHeight={1}
          color={filter === filterName ? 'white' : 'inherit'}
        >
          {value}
        </Typography>
      </Stack>
    </Stack>
  )

  return (
    <Paper sx={panelSx}>
      <Stack
        direction={{ xs: 'column', lg: 'row' }}
        justifyContent="space-between"
        alignItems={{ xs: 'stretch', lg: 'center' }}
        gap={2}
        sx={{
          p: 2,
          bgcolor: '#F8FAFC',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box>
          <Typography variant="title3">Stores and Bags</Typography>
          <Typography variant="body2" color="text.secondary">
            Register medicine stores and operational bags, then assign each bag
            to the capability level it carries.
          </Typography>
        </Box>
        <Button
          startIcon={<AddIcon />}
          color="anGreen"
          onClick={() => {
            setSelectedStore(false)
            setNewLocation('')
            setNewType('Medicine Bag')
            setNewBagLevel(bagLevelOptions[0] ?? 'Intermediate')
            setNewAllowExpired(false)
            setNewAllowAdministration(false)
            setNewBaseLocation(null)
            setAddMode(true)
          }}
          variant="contained"
          size="small"
        >
          Add Location
        </Button>
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} gap={1} sx={{ p: 1.5 }}>
        <LocationStat
          icon={<Inventory2Icon fontSize="small" />}
          label="Locations"
          value={locationStats.total}
          filterName={null}
        />
        <LocationStat
          icon={<BackpackIcon fontSize="small" />}
          label="Medicine bags"
          value={locationStats.bags}
          filterName="bags"
        />
        <LocationStat
          icon={<WarehouseIcon fontSize="small" />}
          label="Stores"
          value={locationStats.stores}
          filterName="stores"
        />
        <LocationStat
          icon={<TuneIcon fontSize="small" />}
          label="Levels in use"
          value={locationStats.levels}
        />
      </Stack>

      <Divider />

      <Box sx={{ p: 2 }}>
        {fetchError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Unable to load medicine locations. Please try again later.
          </Alert>
        )}
        <CustomDataGrid
          rows={sortedRows}
          getRowId={(row) => row._id}
          rowHeight={52}
          sx={{
            height: Math.min(Math.max(sortedRows.length * 52 + 120, 360), 620),
          }}
          columns={[
            { field: 'name', headerName: 'Name', minWidth: 200, flex: 1 },
            {
              field: 'baseLocation',
              headerName: 'Base',
              minWidth: 150,
              flex: 0.6,
              valueGetter: (value) => value?.name ?? '',
              renderCell: ({ value }) =>
                value || (
                  <Chip
                    size="small"
                    variant="outlined"
                    color="error"
                    label="No base"
                  />
                ),
            },
            {
              field: 'type',
              headerName: 'Type',
              headerAlign: 'center',
              minWidth: 150,
              flex: 0.5,
              renderCell: ({ value }) => (
                <Chip
                  size="small"
                  variant="outlined"
                  sx={{ width: '100%' }}
                  icon={
                    value === 'Medicine Store' ? (
                      <WarehouseIcon fontSize="small" />
                    ) : (
                      <BackpackIcon fontSize="small" />
                    )
                  }
                  label={value}
                />
              ),
            },
            {
              field: 'level',
              headerName: 'Level',
              headerAlign: 'center',
              minWidth: 150,
              flex: 0.5,
              renderCell: ({ row, value }) => {
                if (row.type === 'Medicine Store') {
                  return (
                    <Stack direction="row" gap={0.5} width="100%">
                      {row.allowAdministration && (
                        <Chip
                          size="small"
                          variant="outlined"
                          color="info"
                          label="Mobile"
                          sx={{ flex: 1 }}
                        />
                      )}
                      {row.allowExpiredMedicines && (
                        <Chip
                          size="small"
                          variant="outlined"
                          color="warning"
                          label="OOD holding"
                          sx={{ flex: 1 }}
                        />
                      )}
                    </Stack>
                  )
                }
                return value && value !== 'false' ? (
                  <Chip size="small" label={value} sx={{ width: '100%' }} />
                ) : null
              },
            },
          ]}
          slots={{ toolbar: () => null, footer: () => null }}
          disableRowSelectionOnClick
          onRowClick={({ row }) => {
            setSelectedStore(row)
            setNewType(row.type)
            setNewLocation(row.name)
            setNewBagLevel(row?.level)
            setNewAllowExpired(!!row.allowExpiredMedicines)
            setNewAllowAdministration(!!row.allowAdministration)
            setNewBaseLocation(row.baseLocation ?? null)
          }}
        />
      </Box>
      <Modal
        open={addMode || selectedStore}
        onClose={() => {
          setAddMode(false)
          setSelectedStore(false)
        }}
      >
        <Box
          sx={modalStyle}
          component="form"
          onSubmit={(e) => handleSubmitNew(e)}
        >
          <Typography variant="title3">
            {selectedStore ? 'Edit Location' : 'Add Location'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {newType === 'Medicine Bag'
              ? 'Medicine bags can be linked to a bag level for formulary rules.'
              : 'Stores hold stock before it is assigned into operational bags.'}
          </Typography>
          <TextField
            label="Location Name"
            value={newLocation}
            onChange={(e) => setNewLocation(e.target.value)}
            autoComplete="off"
            required
          />
          <SingleBaseLocationSelect
            value={newBaseLocation}
            onChange={setNewBaseLocation}
            helperText="The base this store or bag belongs to. It can be moved later from the location page."
          />
          <Autocomplete
            options={['Medicine Bag', 'Medicine Store']}
            value={newType}
            onChange={(e, newValue) => setNewType(newValue)}
            disableClearable
            renderInput={(params) => (
              <TextField {...params} label="Location Type" required />
            )}
          />
          {newType !== 'Medicine Store' && <Autocomplete
            options={bagLevelOptions}
            value={newBagLevel ?? ''}
            onChange={(e, value) => setNewBagLevel(value)}
            disableClearable
            renderInput={(params) => (
              <TextField
                {...params}
                label="Drug Bag Level"
                helperText="Manage the available levels under Bag Levels settings"
                required
              />
            )}
          />}
          {newType === 'Medicine Store' && (
            <>
              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={newAllowAdministration}
                      onChange={(e) =>
                        setNewAllowAdministration(e.target.checked)
                      }
                    />
                  }
                  label="Mobile store - allow administration"
                />
                <FormHelperText>
                  For stores taken out to events. Medicines can be administered
                  straight from this store, and it is offered alongside bags
                  when recording an administration on the web, mobile app and
                  ePCR.
                </FormHelperText>
              </Box>
              <Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={newAllowExpired}
                      onChange={(e) => setNewAllowExpired(e.target.checked)}
                    />
                  }
                  label="Allow out of date medicines"
                />
                <FormHelperText>
                  For quarantine stores holding expired medicines safely until
                  disposal. Expired medicines in this store are excluded from
                  expiry KPIs, dashboards and alerts.
                </FormHelperText>
              </Box>
            </>
          )}
          <Box display="flex" justifyContent="flex-end">
            <Stack direction="row" gap={1}>
              <Button
                variant="outlined"
                onClick={() => {
                  setAddMode(false)
                  setSelectedStore(false)
                }}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                color="anGreen"
                // onClick={handleSubmitNew}
                type="submit"
              >
                Save
              </Button>
            </Stack>
          </Box>
        </Box>
      </Modal>
      <Snackbar
        open={submitError || submitSuccess}
        autoHideDuration={3000}
        onClose={() => {
          setSubmitError(false)
          setSubmitSuccess(false)
        }}
      >
        <Alert severity={submitError ? 'error' : 'success'}>
          {submitError
            ? `Unable to ${
                selectedStore ? 'edit' : 'add'
              } this medicine location. Please try again later`
            : 'New Medicine Location successfully added'}
        </Alert>
      </Snackbar>
      <Dialog open={!!bagExists} onClose={() => setBagExists(null)}>
        <DialogTitle textAlign="center" color="anBlue.main">
          {newLocation} already exists
        </DialogTitle>
        {bagExists?.archived ? (
          <DialogContent>
            <Stack gap={1}>
              <Typography variant="body2">
                This bag was previously archived.
              </Typography>
              <Stack
                direction={{ xs: 'column', lg: 'row' }}
                gap={{ xs: 1, lg: 3 }}
                width="100%"
                justifyContent="space-between"
              >
                <Button
                  onClick={(e) => handleSubmitNew(e, { undoArchive: true })}
                  color="success"
                  variant="contained"
                  size="small"
                >
                  Undo Archive
                </Button>
                <Button
                  onClick={() => setBagExists(null)}
                  variant="outlined"
                  size="small"
                >
                  Close
                </Button>
              </Stack>
            </Stack>
          </DialogContent>
        ) : (
          <DialogActions>
            <Button
              onClick={() => {
                setBagExists(null)
                setAddMode(false)
                setNewLocation('')
              }}
            >
              Close
            </Button>
          </DialogActions>
        )}
      </Dialog>
    </Paper>
  )
}

export default MedicineStores
